import { Spin, message } from 'antd';
import { cloneDeep } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import AntdIcon from '@ant-design/icons';
import { partitionOptions } from '@components/dimensions/utils/partition';
import FunnelNoDataIcon from '@icons/emp/funnel_no_data.svg';
import InfoIcon from '@icons/emp/info.svg';
import {
  useGetSankeyDimensions,
  useGetUserIdByFunnel,
  useShowFunnel,
} from '@src/emp-3.0/api/queryHooks';
import Configurations, { FUNNEL_STEPS_MIN } from '@src/emp-3.0/components/Funnel/Configurations';
import FilterWrapper from '@src/emp-3.0/components/Funnel/FilterWrapper';
import FunnelTable from '@src/emp-3.0/components/Funnel/FunnelTable';
import { useFunnelStore } from '@src/emp-3.0/components/Funnel/store';
import { hasAppId, removeFunnelEmptyFilters } from '@src/emp-3.0/components/Funnel/utils';
import UsersTable from '@src/emp-3.0/components/Sankey/UsersTable';
import {
  formatFilters,
  formatInterval,
  isInactiveInterval,
} from '@src/emp-3.0/components/Sankey/utils';
import DpiDeprecationAlert from '@src/emp-3.0/components/common/DpiDeprecationAlert';
import FunnelBarChart from '@src/emp-3.0/components/common/FunnelBarChart';
import { isGeoDimension, parseDataToOptions } from '@src/emp-3.0/utils/parse';
import {
  trackGenerateFunnel,
  trackGenerateFunnelFailure,
  trackGenerateFunnelSuccess,
  trackViewUserTimelineFromFunnel,
} from '@src/sensor';
import { COLORS } from '@src/emp-3.0/utils/constants';
import { useInitialInfoStore } from '@src/emp-3.0/store/useInitialInfo';
import styles from '../Sankey/index.less';
import isEmpty from 'lodash/isEmpty';
import queryString from 'query-string';
import { useGoToUserTimeline } from '@src/hooks/useGoToUserTimeline';
import usePageTitle from '@src/hooks/usePageTitle';
import { ESystemType } from '@src/emp-3.0/types';
import { defaultCalenderBoundry } from '../Sankey/constants';
// import { usePageInfoStore } from '@pulse/nav';
const EMPTY_ARRAY = [];
export const OutOfRetentionMessage = 'Data is available for last 1 month only.';
const FunnelPage = (initPayload) => {
  // usePageInfoStore.setState({
  //   productName: 'ECO',
  //   logoHref: '/app/app-insights'
  // });
  usePageTitle({ text: 'Funnels' });
  const onGoToUserTimeline = useGoToUserTimeline();
  const { userInfo, trendsCalenderBoundry: calenderBoundry } = useInitialInfoStore();
  const { mutate: getFunnel, isLoading: isGetFunnelLoading } = useShowFunnel();
  const { mutate: getUserId, isLoading: isGetUserIdLoading } = useGetUserIdByFunnel();
  const { data: funnelDimensionList = EMPTY_ARRAY, isLoading: dimensionLoading } =
    useGetSankeyDimensions();
  const breakDownDimensionOptions =
    parseDataToOptions(
      funnelDimensionList.filter((item) => item.can_funnel_breakdown === 1),
      {
        label: 'display_name',
        value: 'dimension_name',
      },
    ) || [];
  const { share_id } = queryString.parse(window.location.search);
  const isFromShare = !isEmpty(share_id);
  const {
    funnelRequestBody,
    setFunnelRequestBody,
    userIds,
    setUserIds,
    funnelData,
    setFunnelData,
    generateFunnelRequestBody,
    setGenerateFunnelRequestBody,
  } = useFunnelStore((state) => state);
  const dataAccessMonths = userInfo.data_access_months || 13;
  const removeEmptyFilters = (payloadPartial = {}) => {
    return removeFunnelEmptyFilters({ ...funnelRequestBody, ...payloadPartial });
  };
  const [fetched, setFetched] = useState(false);
  const [selectedFieldValue, setSelectedFieldValue] = useState<string[]>([]);

  const user_id_key = 'client_id';

  const { filters, funnel_events, dimension_breakdowns } = funnelRequestBody;

  const hasFunnelData = funnelData?.funnel_data?.length > 0;

  const invalidParams = useMemo(() => {
    if (funnel_events?.filter((event) => event.event_name !== '').length < FUNNEL_STEPS_MIN) {
      return true;
    }
    return false;
  }, [funnel_events, filters]);

  const outOfRetention = useMemo(() => {
    const start = generateFunnelRequestBody?.interval?.split('/')[0];
    const limitStart = moment().startOf('day').subtract(dataAccessMonths, 'months');
    if (moment(start).isBefore(limitStart)) {
      return true;
    }
  }, [generateFunnelRequestBody, dataAccessMonths]);

  const filteredFunnelData = useMemo(() => {
    const { funnel_data = [], event_names } = funnelData;
    let newFunnelData = funnel_data;
    if (selectedFieldValue.length === 0) {
      if (funnel_data[0]) {
        newFunnelData = [funnel_data[0]];
      }
    } else {
      const dimension = dimension_breakdowns?.[0]?.[0]?.field;
      const field = isGeoDimension(dimension) ? `${dimension}_code` : dimension;
      newFunnelData =
        field === ''
          ? funnel_data
          : funnel_data.filter(
              (item, index) => index !== 0 && selectedFieldValue.includes(item[field]),
            );
    }
    return {
      event_names,
      funnel_data: newFunnelData,
    };
  }, [funnelData, selectedFieldValue, dimension_breakdowns]);

  const fetchFunnelData = (payloadPartial?) => {
    const payload = removeEmptyFilters(payloadPartial);
    if (!payload) {
      return;
    }
    setGenerateFunnelRequestBody(cloneDeep(payload));
    trackGenerateFunnel({ ...payload });
    getFunnel(
      { payload },
      {
        onSuccess: (data) => {
          trackGenerateFunnelSuccess({ ...payload });
          setFetched(true);
          if (data) {
            setFunnelData(data.data);
          } else {
            setFunnelData({
              funnel_data: [],
              event_names: [],
            });
          }
          // fetchUsers([]);
        },
        onError: (error: any) => {
          trackGenerateFunnelFailure({ ...payload });
          setFetched(true);
          message.error(error?.response?.data?.msg).then();
        },
      },
    );
  };

  const { defaultOptions: globalFilterDimensionOptions } = partitionOptions(
    funnelDimensionList.filter((item) => item.can_filter === 1),
    (dimension) => filters.some((item) => item[0].field === dimension.dimension_name),
  );

  useEffect(() => {
    if (isFromShare) {
      return;
    }

    if (hasFunnelData && initPayload?.from !== 'sankey') {
      return;
    }
    if (dimensionLoading) {
      return;
    }
    const { globalFilter, interval, funnelEvents, filters } = initPayload;
    const parentFilters = filters || globalFilter;
    let usefulFilters = funnelRequestBody.filters;
    if (parentFilters?.length > 0) {
      usefulFilters = formatFilters(parentFilters, funnelDimensionList);
    }
    const intervalFormat = formatInterval(interval, calenderBoundry);
    const payload = {
      filters: usefulFilters,
      interval: intervalFormat,
      ...(funnelEvents && { funnel_events: funnelEvents }),
      dimension_breakdowns: funnelRequestBody.dimension_breakdowns,
    };
    setFunnelRequestBody({ ...payload });
    // if there are three breakpoints and the filters are not empty, then fetch the funnel data
    if (funnelEvents?.length >= FUNNEL_STEPS_MIN && usefulFilters.length > 0) {
      fetchFunnelData(payload);
    }
  }, [initPayload, dimensionLoading, funnelDimensionList, calenderBoundry]);

  const handleChange = (values) => {
    const { filters, interval } = values;
    const payloadPartial = {
      ...(filters && { filters }),
      ...((interval || interval === '') && { interval }),
    };
    setFunnelRequestBody(payloadPartial);
    fetchFunnelData(payloadPartial);
  };
  const hasValueBreakdown = generateFunnelRequestBody?.dimension_breakdowns?.filter(
    (item) => item.filter((x) => x.value?.length).length,
  );
  const curBreakDownFields = hasValueBreakdown?.map((item) => item[0].field)[0];
  const funnelBarChartFields = selectedFieldValue.length === 0 ? '' : curBreakDownFields;
  const selectedFieldValueColors = useMemo(() => {
    if ((selectedFieldValue ?? []).length === 0) return COLORS;
    const colors: string[] = [];
    (funnelData?.funnel_data ?? []).forEach((item, index) => {
      const field = isGeoDimension(curBreakDownFields)
        ? `${curBreakDownFields}_code`
        : curBreakDownFields;
      if (selectedFieldValue.includes(item[field]) && index !== 0) {
        colors.push(COLORS[index - 1]);
      }
    });
    return colors;
  }, [curBreakDownFields, funnelData?.funnel_data, selectedFieldValue]);

  const fetchUsers = (breakdowns) => {
    const nonEmptyBreakdowns = breakdowns.filter(Boolean);
    setSelectedFieldValue(breakdowns);
    const payload = removeEmptyFilters({
      ...generateFunnelRequestBody,
      target_field: user_id_key,
      limit: 500,
      dimension_breakdowns: undefined,
    });
    if (!payload) {
      return;
    }
    if (nonEmptyBreakdowns?.length > 0) {
      payload.dimension_breakdown_filters = nonEmptyBreakdowns.map((item) => {
        return [
          {
            field: curBreakDownFields,
            op: 'eq',
            value: item.toString(),
          },
        ];
      });
    } else {
      payload.dimension_breakdown_filters = [];
    }

    getUserId(
      { payload },
      {
        onSuccess: (data) => {
          setUserIds(data?.data || []);
        },
        onError: (error: any) => {
          message.error(error?.response?.data?.msg).then();
        },
      },
    );
  };

  const Empty = () => {
    if (!hasFunnelData && (invalidParams || !fetched)) {
      return (
        <div
          style={{
            color: 'rgba(0, 0, 0, 0.25)',
            height: '100%',
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
            fontSize: '20px',
            fontWeight: 500,
          }}
        >
          <AntdIcon style={{ fontSize: '16px', marginRight: '10px' }} component={InfoIcon} />
          Please select Steps to generate funnel chart
        </div>
      );
    }
    return (
      <div className={styles.noData}>
        <AntdIcon style={{ fontSize: 104, marginBottom: 12 }} component={FunnelNoDataIcon} />
        <div>
          {outOfRetention
            ? OutOfRetentionMessage.replace('1', dataAccessMonths + '')
            : 'No data available to create a Funnel diagram.'}
        </div>
        <div>Please adjust the configurations and try again.</div>
      </div>
    );
  };
  const breakdownsLength =
    generateFunnelRequestBody?.dimension_breakdowns?.[0]?.[0].value.length || 1;
  const minwidthOfChart =
    40 * (generateFunnelRequestBody?.funnel_events?.length || 1) * breakdownsLength + 100;

  const eventNames = funnel_events?.map((item) => item.event_name) || [];

  const mask = useMemo(() => {
    return isInactiveInterval(generateFunnelRequestBody?.interval, calenderBoundry);
  }, [generateFunnelRequestBody?.interval, calenderBoundry]);
  const disableStyle = mask ? { opacity: 0.6, pointerEvents: 'none' } : {};
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px)',
      }}
    >
      <DpiDeprecationAlert feature="Funnels" />
      <div style={{ background: '#fff', width: '100%', marginBottom: 8 }}>
        <FilterWrapper
          dimensionOptions={globalFilterDimensionOptions}
          onChange={handleChange}
          mask={mask}
          calenderBoundry={calenderBoundry}
        />
      </div>
      <div style={{ background: '#fff', display: 'flex', flex: 1, width: '100%' }}>
        <Configurations
          onFetch={fetchFunnelData}
          loading={isGetFunnelLoading}
          invalidParams={invalidParams}
          disableStyle={disableStyle}
        />
        <div style={{ flex: 1, padding: '0 24px', overflow: 'scroll' }}>
          <div
            style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              borderBottom: '1px solid rgba(0, 0, 0, 0.15)',
              padding: '16px 0',
              marginBottom: '24px',
            }}
          >
            <span style={{ flex: 1 }}>Funnel Analysis </span>
          </div>

          {hasFunnelData && !outOfRetention ? (
            <Spin spinning={isGetFunnelLoading}>
              <FunnelBarChart
                style={{ minWidth: minwidthOfChart }}
                type="funnel"
                funnelRes={filteredFunnelData}
                field={funnelBarChartFields}
                colors={selectedFieldValueColors}
              />
              <FunnelTable
                style={{ marginBottom: '24px', minWidth: minwidthOfChart }}
                data={funnelData}
                options={breakDownDimensionOptions}
                onChecked={fetchUsers}
                breakdowns={hasValueBreakdown}
                disableCheckbox={mask}
              />
            </Spin>
          ) : (
            <Empty />
          )}

          {hasFunnelData ? (
            <Spin spinning={isGetUserIdLoading} style={{ width: '100%' }}>
              <UsersTable
                type="funnel"
                userKey={user_id_key}
                dataSource={userIds || []}
                handleEvent={(event, obj) => {
                  trackViewUserTimelineFromFunnel({ ...obj });
                  onGoToUserTimeline?.(event, obj);
                }}
                interval={generateFunnelRequestBody?.interval ?? ''}
                disableStyle={disableStyle}
                filters={generateFunnelRequestBody?.filters ?? []}
                selectedNodeNames={eventNames}
              />
            </Spin>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default FunnelPage;
